import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';

type Props = {
	border?: boolean;
	children: ReactNode;
};

/**
 * @deprecated This component is deprecated. Use ADS Skeleton component or roll your own loading wrapper.
 * See package README for migration guidance.
 */
export default function Blanket(props: Props): React.JSX.Element {
	const { border, children } = props;

	return fg('corcap-4455-increase-ads-adoption') ? (
		<Inline xcss={styles.blanketContainer}>
			<BackgroundContainer>
				<Background border={border} />
			</BackgroundContainer>

			<Box xcss={styles.componentContainer}>{children}</Box>
			{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
			<SpinnerContainer role="status" aria-label="Loading">
				<Spinner />
			</SpinnerContainer>
		</Inline>
	) : (
		<StyledBlanketContainer>
			<BackgroundContainer>
				<Background border={border} />
			</BackgroundContainer>
			<StyledComponentContainer>{children}</StyledComponentContainer>
			{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
			<SpinnerContainer role="status" aria-label="Loading">
				<Spinner />
			</SpinnerContainer>
		</StyledBlanketContainer>
	);
}

Blanket.defaultProps = {
	border: true,
};

const styles = {
	blanketContainer: xcss({
		position: 'relative',
		height: 'auto',
		display: 'inline-flex',
		flexDirection: 'row',
	}),
	componentContainer: xcss({
		opacity: token('opacity.loading'),
		flex: '1 1 auto',
	}),
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledBlanketContainer = styled.div({
	position: 'relative',
	display: 'inline-flex',
	flexDirection: 'row',
	height: 'auto',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpinnerContainer = styled.div({
	position: 'absolute',
	top: 0,
	left: 0,
	height: '100%',
	width: '100%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	boxSizing: 'border-box',
	paddingRight: token('space.100'),
	paddingLeft: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BackgroundContainer = styled.div({
	position: 'absolute',
	height: '100%',
	width: '100%',
	boxSizing: 'border-box',
	paddingTop: token('space.025'),
	paddingRight: token('space.025'),
	paddingBottom: token('space.025'),
	paddingLeft: token('space.025'),
	overflow: 'auto',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Background = styled.div<{ border?: boolean }>({
	position: 'relative',
	height: '100%',
	width: '100%',
	boxSizing: 'border-box',
	backgroundColor: `${token('color.background.accent.gray.subtlest')}`,
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	border: `${(props: { border?: boolean }) =>
		props.border ? `1px solid ${token('color.border.disabled')}` : 'none'}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledComponentContainer = styled.div({
	opacity: 0.2,
	flex: '1 1 auto',
});
